import { Box, Text } from '@ltvco/refresh-lib/theme';
import {
  type ApiTransactionRow,
  useApiMonthlyUsage,
  useApiTransactions,
} from '@ltvco/refresh-lib/v2';
import { type SyntheticEvent, useMemo, useState } from 'react';

import { UsageTab, UsageTabs } from './components';
import { MonthlyUsageTabPanel } from './MonthlyUsageTabPanel';
import { TransactionsTabPanel } from './TransactionsTabPanel';

const SECTION_TITLE = 'API Usage History';

export function ApiUsageSection() {
  const [tab, setTab] = useState(0);
  const [page, setPage] = useState(1);
  const perPage = 20;

  const monthlyQuery = useApiMonthlyUsage();
  const transactionsQuery = useApiTransactions(page, perPage);

  const monthlyRows = monthlyQuery.data?.monthlyUsage ?? [];
  const transactions: ApiTransactionRow[] =
    transactionsQuery.data?.transactions ?? [];
  const meta = transactionsQuery.data?.meta;

  const monthlySorted = useMemo(
    () =>
      [...monthlyRows].sort((a, b) =>
        a.month < b.month ? 1 : a.month > b.month ? -1 : 0
      ),
    [monthlyRows]
  );

  const handleTabChange = (_: SyntheticEvent, next: number) => {
    setTab(next);
    setPage(1);
  };

  const decrementPage = () => {
    setPage((prev) => Math.max(1, prev - 1));
  };

  const incrementPage = () => {
    setPage((prev) => prev + 1);
  };

  const monthlyLoading = monthlyQuery.isLoading;
  const monthlyError = monthlyQuery.isError;
  const transactionsLoading = transactionsQuery.isLoading;
  const transactionsError = transactionsQuery.isError;

  const showMonthlyError = tab === 0 && monthlyError;
  const showTransactionsError = tab === 1 && transactionsError;

  return (
    <Box>
      <Text variant="h4" fontWeight="bold" sx={{ mb: 2 }}>
        {SECTION_TITLE}
      </Text>

      <UsageTabs value={tab} onChange={handleTabChange} variant="fullWidth">
        <UsageTab label="Monthly Usage" />
        <UsageTab label="Transactions" />
      </UsageTabs>

      {showMonthlyError || showTransactionsError ? (
        <Text color="error" sx={{ mt: 2 }}>
          Unable to load usage history. Try again later.
        </Text>
      ) : null}

      {tab === 0 && (
        <MonthlyUsageTabPanel
          isLoading={monthlyLoading}
          monthlySorted={monthlySorted}
        />
      )}

      {tab === 1 && (
        <TransactionsTabPanel
          isLoading={transactionsLoading}
          transactions={transactions}
          meta={meta}
          onPrevPage={decrementPage}
          onNextPage={incrementPage}
        />
      )}
    </Box>
  );
}
